import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, RefreshCw, AlertTriangle, Clock, Users, Building2, Target, ChevronRight, TrendingUp,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { apqpService } from '../services/api';

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
const daysUntil = (iso) => {
  if (!iso) return null;
  const diff = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
  return diff;
};

export default function ApqpDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apqpService.dashboard();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const workload = data?.workload || [];
  const milestones = data?.upcoming_milestones || [];
  const lateProjects = data?.late_projects || [];
  const byClient = data?.by_client || [];
  const byStatus = data?.by_status || [];

  const maxWorkload = Math.max(1, ...workload.map(w => w.active));
  const maxClient = Math.max(1, ...byClient.map(c => c.count));

  return (
    <>
      <div className="space-y-4" data-testid="apqp-dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Dashboard APQP/PPAP</h1>
              <p className="text-sm text-slate-500">Cruscotto trasversale su tutti i progetti aperti</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-apqp-dashboard-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" onClick={() => navigate('/apqp-projects')}>
              Registro progetti <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Progetti aperti" value={totals.open ?? '—'} color="bg-slate-50 border-slate-200" data-testid="dash-total-open" />
          <StatCard label="In corso" value={totals.in_progress ?? 0} color="bg-sky-50 border-sky-200" />
          <StatCard label="In attesa" value={totals.waiting ?? 0} color="bg-purple-50 border-purple-200" />
          <StatCard label="In ritardo" value={totals.late ?? 0} color="bg-red-50 border-red-200" valueColor="text-red-700" data-testid="dash-total-late" />
          <StatCard label="Totale progetti" value={totals.total ?? 0} color="bg-emerald-50 border-emerald-200" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Workload responsabili */}
          <Card className="lg:col-span-2">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold text-slate-800">Carico responsabili</h3>
                <span className="text-xs text-slate-400">(attività attive / scadute)</span>
              </div>
              {workload.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">Nessuna attività attiva.</p>
              ) : (
                <div className="space-y-2" data-testid="workload-list">
                  {workload.map(w => (
                    <div key={w.responsible} className="flex items-center gap-2 text-xs">
                      <div className="w-40 text-slate-700 truncate">{w.responsible}</div>
                      <div className="flex-1 h-4 bg-slate-100 rounded relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-sky-400"
                          style={{ width: `${(w.active / maxWorkload) * 100}%` }}
                        />
                        {w.overdue > 0 && (
                          <div
                            className="absolute inset-y-0 left-0 bg-red-500"
                            style={{ width: `${(w.overdue / maxWorkload) * 100}%` }}
                            title={`${w.overdue} scadute`}
                          />
                        )}
                      </div>
                      <div className="w-24 text-right font-mono text-slate-600">
                        <span className="text-sky-700">{w.active}</span>
                        {w.overdue > 0 && <span className="text-red-600 ml-2">⚠ {w.overdue}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Progetti per cliente */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-slate-500" />
                <h3 className="font-semibold text-slate-800">Progetti per cliente</h3>
              </div>
              {byClient.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">—</p>
              ) : (
                <div className="space-y-1.5" data-testid="by-client-list">
                  {byClient.map(c => (
                    <div key={c.client} className="flex items-center gap-2 text-xs">
                      <div className="w-32 text-slate-700 truncate">{c.client}</div>
                      <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                        <div className="h-full bg-indigo-400" style={{ width: `${(c.count / maxClient) * 100}%` }} />
                      </div>
                      <div className="w-8 text-right font-mono text-slate-600">{c.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Milestones imminenti */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-slate-800">Milestone nei prossimi 30 giorni</h3>
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] border">{milestones.length}</Badge>
              </div>
              {milestones.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center" data-testid="milestones-empty">Nessuna milestone in scadenza.</p>
              ) : (
                <ul className="divide-y" data-testid="milestones-list">
                  {milestones.slice(0, 12).map(m => {
                    const d = daysUntil(m.planned_end);
                    const urgencyClass = d <= 3 ? 'text-red-600' : d <= 7 ? 'text-amber-600' : 'text-slate-500';
                    return (
                      <li key={m.id} className="py-2 text-xs flex items-start gap-2 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/apqp-projects/${m.project_id}`)}>
                        <Clock className={`w-4 h-4 mt-0.5 ${urgencyClass}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">◆ {m.name}</div>
                          <div className="text-slate-500 truncate">{m.project_number} · {m.project_title} {m.client_name && `· ${m.client_name}`}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono">{fmt(m.planned_end)}</div>
                          <div className={`text-[10px] ${urgencyClass}`}>tra {d}gg</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Progetti in ritardo */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <h3 className="font-semibold text-slate-800">Progetti in ritardo</h3>
                <Badge className="bg-red-100 text-red-700 border-red-300 text-[10px] border">{lateProjects.length}</Badge>
              </div>
              {lateProjects.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center" data-testid="late-empty">Nessun progetto in ritardo.</p>
              ) : (
                <ul className="divide-y" data-testid="late-list">
                  {lateProjects.slice(0, 10).map(p => (
                    <li key={p.id} className="py-2 text-xs flex items-start gap-2 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/apqp-projects/${p.id}`)}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{p.title}</div>
                        <div className="text-slate-500 truncate font-mono">{p.project_number} · {p.client_name || '—'}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-red-600">+{p.late_days}gg</div>
                        <div className="text-[10px] text-slate-400">{p.progress_percent}%</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* By status distribution */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <h3 className="font-semibold text-slate-800">Distribuzione per stato</h3>
            </div>
            {byStatus.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">—</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="by-status-list">
                {byStatus.map(s => (
                  <Badge key={s.status} className="bg-slate-100 text-slate-700 border-slate-300 border text-xs px-3 py-1">
                    {s.status} <span className="ml-1.5 font-bold">{s.count}</span>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

const StatCard = ({ label, value, color, valueColor, ...rest }) => (
  <div className={`border rounded-lg p-3 ${color}`} {...rest}>
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className={`text-2xl font-bold ${valueColor || 'text-slate-900'}`}>{value}</div>
  </div>
);
